// Shared role read-surface display helpers (status badge + lock label + the lock
// vocabulary), reused by the list, peek, detail, create wizard, and edit
// drawer so they read identically and can't drift (mirror userDisplay). RoleStatus
// is the role's lifecycle for the entityRegistry action predicate.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, type BadgeAppearance } from '../../components/ui/Badge.js';
import { codeSource, type CodeSource, type OptionRow } from '../../hooks/useOptions.js';

/** A roles-namespace translator (loose signature so react-i18next's `t` assigns). */
export type Tr = (key: string, opts?: Record<string, unknown>) => string;

/** The role lifecycle status — drives the action registry's `show(ctx)` predicate. */
export type RoleStatus = 'active' | 'disabled';
export function roleStatus(role: { is_disabled: boolean }): RoleStatus {
  return role.is_disabled ? 'disabled' : 'active';
}

export function RoleStatusBadge({ disabled, appearance = 'subtle' }: { disabled: boolean; appearance?: BadgeAppearance }) {
  const { t } = useTranslation('roles');
  const label = disabled ? t('status.disabled') : t('status.active');
  return (
    <Badge
      variant={disabled ? 'warning' : 'success'}
      appearance={appearance}
      title={appearance === 'dot' ? label : undefined}
    >
      {label}
    </Badge>
  );
}

/** The inactivity-lock display string ("Locks after N min idle" / "No auto-lock"). */
export function lockLabel(t: Tr, minutes: number | null): string {
  return minutes ? t('lock.after', { minutes }) : t('lock.none');
}

/** The lock's "no auto-lock" row — the field's own id for null (`lockValue` / `lockMinutes` cross the boundary). */
const LOCK_NONE = 'none';
export const lockValue = (minutes: number | null): string => (minutes ? String(minutes) : LOCK_NONE);
export const lockMinutes = (value: string | null): number | null => (value && value !== LOCK_NONE ? Number(value) : null);

/** The inactivity lock as a vocabulary (ADR-080 — the code kind): "No auto-lock", then the presets in order, with the
 *  role's OWN minutes among them when they are not a preset (the Dispatch Lead's 20 — a clone's) — a normal row in
 *  its place, never "not available". Shared by the create wizard and the edit drawer. */
export function useLockSource(presets: readonly number[], held: number | null): CodeSource {
  const { t, i18n } = useTranslation('roles');
  return useMemo(() => {
    const minutes = [...new Set<number>([...presets, ...(held ? [held] : [])])].sort((a, b) => a - b);
    const rows: OptionRow[] = [
      { id: LOCK_NONE, label: t('lock.none'), pickable: true, group: null },
      ...minutes.map((m) => ({ id: String(m), label: m === 60 ? t('lock.hour') : t('lock.minutes', { count: m }), pickable: true, group: null })),
    ];
    return codeSource(`role.lock:${i18n.language}:${held ?? ''}`, rows);
  }, [t, i18n.language, presets, held]);
}
